<template>
  <div class="mobile-container">
    <div class="search">
      <input v-model="query" @keyup.enter="search" placeholder="CIQ ID or Ticker" />
      <button @click="search">Search</button>
    </div>

    <div v-if="loading" class="loader">Loading…</div>
    <div v-if="error" class="error">{{ error }}</div>

    <div v-if="hasResults" class="sections">
      <div v-if="companyIds.length > 0" class="card">
        <h3>Companies in Comparison</h3>
        <div class="company-tags">
          <span v-for="(company, index) in (Array.isArray(companyNames) ? companyNames : [])" :key="index" class="company-tag">
            {{ company }} ({{ symbols[index] }})
          </span>
        </div>
      </div>

      <div v-if="researchReports.length > 0" class="card">
        <h3>Research Reports</h3>
        <div class="comparison-section">
          <div v-for="(report, index) in researchReports" :key="index" class="company-report">
            <h4>{{ (Array.isArray(companyNames) && companyNames[index]) || symbols[index] || 'Company' }} ({{ symbols[index] || 'N/A' }})</h4>
            
            <div class="report-section">
              <h5>Summary</h5>
              <div class="content" v-html="report.summary || 'No summary available'"></div>
            </div>
            
            <div class="report-section">
              <h5>Upside Scenario</h5>
              <div class="content" v-html="report.upsideScenario || 'No upside scenario available'"></div>
              <ul v-if="report.upsideScenarioCases && report.upsideScenarioCases.length > 0">
                <li v-for="(item, i) in report.upsideScenarioCases" :key="i">{{ item }}</li>
              </ul>
            </div>
            
            <div class="report-section">
              <h5>Downside Scenario</h5>
              <div class="content" v-html="report.downsideScenario || 'No downside scenario available'"></div>
              <ul v-if="report.downsideScenarioCases && report.downsideScenarioCases.length > 0">
                <li v-for="(item, i) in report.downsideScenarioCases" :key="i">{{ item }}</li>
              </ul>
            </div>
            
            <div class="report-section">
              <h5>Key Strengths</h5>
              <div v-if="!report.keys?.errMsg">
                <ul>
                  <li v-for="(k, i) in filteredKeyStrengths(report.keys)" :key="i">
                    {{ k.keyStrength }}
                  </li>
                </ul>
              </div>
              <div v-else>
                <p>{{ report.keys.errMsg }}</p>
              </div>
            </div>
            
            <div class="report-section">
              <h5>Key Risks</h5>
              <div v-if="!report.keys?.errMsg">
                <ul>
                  <li v-for="(k, i) in filteredKeyRisks(report.keys)" :key="i">
                    {{ k.keyRisk }}
                  </li>
                </ul>
              </div>
            </div>
          </div>
        </div>
      </div>

      <div v-if="periodDate.length > 0" class="card">
        <h3>Financial Data</h3>
        <div class="compact-table-wrapper">
          <table class="compact-table">
            <thead>
              <tr>
                <th>Metric</th>
                <th v-for="(date, index) in periodDate" :key="index">
                  <div class="header-ticker">{{ symbols[index] || 'N/A' }}</div>
                  <div class="header-fy">FY{{ formatYear(date) }}</div>
                </th>
              </tr>
            </thead>
            <tbody>
              <tr>
                <td>Total Revenue</td>
                <td v-for="(value, index) in totalRevenue" :key="index" class="number">
                  {{ formatNumber(value) }}
                </td>
              </tr>
              <tr>
                <td>Net Income</td>
                <td v-for="(value, index) in netIncome" :key="index" class="number">
                  {{ formatNumber(value) }}
                </td>
              </tr>
              <tr>
                <td>Gross Margin %</td>
                <td v-for="(value, index) in grossMargin" :key="index" class="number">
                  {{ formatPercentage(value) }}
                </td>
              </tr>
              <tr>
                <td>Return on Asset %</td>
                <td v-for="(value, index) in returnOnAsset" :key="index" class="number">
                  {{ formatPercentage(value) }}
                </td>
              </tr>
              <tr>
                <td>Return on Equity %</td>
                <td v-for="(value, index) in returnOnEquity" :key="index" class="number">
                  {{ formatPercentage(value) }}
                </td>
              </tr>
              <tr>
                <td>Total Debt/Equity</td>
                <td v-for="(value, index) in totalDebtEquity" :key="index" class="number">
                  {{ formatNumber(value) }}
                </td>
              </tr>
            </tbody>
          </table>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import dashboard7Api from '@/api/dashboard7.js';
import moment from 'moment';
const xpath = require('xpath');
const dom = require('@xmldom/xmldom').DOMParser;
const Buffer = require('buffer').Buffer;

export default {
  name: 'Dashboard7Mobile',
  data() {
    return {
      query: '',
      loading: false,
      error: null,
      companyIds: [],
      symbols: [],
      companyNames: [],
      researchReports: [],
      periodDate: [],
      totalRevenue: [],
      netIncome: [],
      grossMargin: [],
      returnOnAsset: [],
      returnOnEquity: [],
      totalDebtEquity: [],
    };
  },
  computed: {
    hasResults() {
      return this.companyIds.length > 0 || 
             this.researchReports.length > 0 || 
             this.periodDate.length > 0;
    }
  },
  methods: {
    async search() {
      if (!this.query.trim()) {
        this.error = 'Please enter a CIQ ID or Ticker';
        return;
      }
      
      this.loading = true;
      this.error = null;
      
      try {
        // Reset all data
        this.companyIds = [];
        this.symbols = [];
        this.companyNames = [];
        this.researchReports = [];
        this.periodDate = [];
        this.totalRevenue = [];
        this.netIncome = [];
        this.grossMargin = [];
        this.returnOnAsset = [];
        this.returnOnEquity = [];
        this.totalDebtEquity = [];

        // Get ticker for the query
        let ticker;
        try {
          ticker = await dashboard7Api.ticker(this.query);
          if (!ticker) ticker = this.query;
        } catch {
          ticker = this.query;
        }
        
        // Get quick comparisons (peers)
        let symbols = [];
        try {
          symbols = await dashboard7Api.quickComps(this.query);
          if (!Array.isArray(symbols)) symbols = [];
        } catch {
          symbols = [];
        }
        
        // Remove duplicates
        symbols = [...new Set(symbols)];
        
        // Restrict to 4 items
        symbols = symbols.slice(0, 4);
        
        // Add ticker at the beginning if it's not already included
        if (ticker && !symbols.includes(ticker)) {
          symbols.unshift(ticker);
        }
        
        // Reverse order exchange:symbol => symbol:exchange
        symbols = symbols.map(symbol => {
          if (!symbol) return null;
          return symbol.includes(':') ? symbol.split(':').reverse().join(':') : symbol;
        }).filter(Boolean);
        
        if (symbols.length === 0) {
          this.error = 'No companies found for the given query';
          this.loading = false;
          return;
        }
        
        this.symbols = symbols;
        
        // Get company IDs
        try {
          const companyIds = await dashboard7Api.companyIds(symbols);
          this.companyIds = Array.isArray(companyIds) ? companyIds : [];
        } catch {
          this.companyIds = [];
        }
        
        // Get company names
        try {
          const idsToUse = this.companyIds.length > 0 ? this.companyIds : symbols;
          const companyNames = await dashboard7Api.companyNames(idsToUse);
          this.companyNames = Array.isArray(companyNames) ? companyNames : symbols;
        } catch {
          this.companyNames = symbols;
        }
        
        // Ensure companyNames is an array with the same length as symbols
        if (!Array.isArray(this.companyNames)) {
          this.companyNames = symbols;
        }
        while (this.companyNames.length < symbols.length) {
          this.companyNames.push(symbols[this.companyNames.length]);
        }
        
        // Get period dates
        try {
          const idsForPeriod = this.companyIds.length > 0 ? this.companyIds : symbols;
          const periodDates = await dashboard7Api.fetchPeriodDates(idsForPeriod);
          if (Array.isArray(periodDates)) {
            this.periodDate = periodDates.map(d => {
              try {
                return d ? moment(d, 'MM/DD/YYYY') : null;
              } catch {
                return null;
              }
            }).filter(Boolean);
          } else {
            this.periodDate = [];
          }
        } catch {
          this.periodDate = [];
        }
        
        // Fetch data for each company
        for (let i = 0; i < symbols.length; i++) {
          const symbol = symbols[i];
          const companyId = this.companyIds[i] || symbol;
          
          // Fetch research report
          try {
            const researchReport = await this.fetchRXResearch(companyId);
            this.researchReports.push(researchReport);
          } catch {
            this.researchReports.push({
              summary: '<p>No research report available.</p>',
              upsideScenario: '<p>No upside scenario available.</p>',
              downsideScenario: '<p>No downside scenario available.</p>',
              keys: { errMsg: 'No research report available.' }
            });
          }
          
          // Fetch financials
          try {
            const financials = await dashboard7Api.fetchFinancials(companyId);
            this.totalRevenue.push(parseFloat(financials?.totalRevenue) || 0);
            this.netIncome.push(parseFloat(financials?.netIncome) || 0);
            this.grossMargin.push(parseFloat(financials?.grossMargin) || 0);
            this.returnOnAsset.push(parseFloat(financials?.returnOnAsset) || 0);
            this.returnOnEquity.push(parseFloat(financials?.returnOnEquity) || 0);
            this.totalDebtEquity.push(parseFloat(financials?.totalDebtEquity) || 0);
          } catch {
            this.totalRevenue.push(0);
            this.netIncome.push(0);
            this.grossMargin.push(0);
            this.returnOnAsset.push(0);
            this.returnOnEquity.push(0);
            this.totalDebtEquity.push(0);
          }
        }
        
      } catch (err) {
        console.error('Error in search:', err);
        this.error = 'Failed to fetch data. Please check your connection and try again.';
      } finally {
        this.loading = false;
      }
    },
    
    decodeHTML(html) {
      const txt = document.createElement('textarea');
      txt.innerHTML = html;
      return txt.value;
    },
    
    async fetchRXResearch(companyId) {
      try {
        let response = await dashboard7Api.fetchArticleList(companyId);
        if (response.error) {
          return {
            errMsg: `No full research report exists for companyId: ${companyId}`,
          };
        }
        if (response.length == 0) {
          return {
            errMsg: 'No research report available.',
          };
        }
        const articleId = response[0].articleId;
        response = await dashboard7Api.fetchArticleContent(articleId);
        if (response.error) {
          return {
            errMsg: `Failed to read content for articleId: ${articleId}`,
          };
        }

        // response should be a base64 string
        const buffer = Buffer.from(response, 'base64');
        const xml = buffer.toString('utf-8');
        const doc = new dom().parseFromString(xml, 'text/xml');

        // key strengths / key risks
        let rows = xpath.select(
          '/page/ROWSET/ARTICLE/spdoc/analysis/body/section[@name="Credit Highlights"]/section[1]/table[1]/tablerow',
          doc
        );
        let contentIndex = 2; // bascially the key strengths / risks content will start from index = 2.
        if (rows.length === 0) {
          rows = xpath.select(
            '/page/ROWSET/ARTICLE/spdoc/analysis/body/section[@name="Credit Highlights"]/table[1]/tablerow',
            doc
          );
          contentIndex = 3;
        }
        const report = {};
        let keys = [];
        rows.forEach((row, index) => {
          if (index < contentIndex) return;
          const k = {
            keyStrength: this.decodeHTML(row.childNodes[1].textContent || ''),
            keyRisk: this.decodeHTML(row.childNodes[3].textContent || ''),
          };
          keys.push(k);
        });
        if (keys.length === 0) {
          keys = {
            errMsg: '(Key Strength / Risk section does not exist.)',
          };
        }
        report.keys = keys;

        // summary
        rows = xpath.select(
          '/page/ROWSET/ARTICLE/spdoc/analysis/body/section[@name="Outlook"]/para/text()',
          doc
        );
        const summary =
          rows.length > 0
            ? rows.reduce((acc, row) => acc + '<p>' + this.decodeHTML(row.nodeValue || '') + '</p>', '')
            : '<p>No summary available</p>';
        report.summary = summary;

        // downside scenario
        rows = xpath.select(
          '/page/ROWSET/ARTICLE/spdoc/analysis/body/section[@name="Outlook"]/section[normalize-space(@name)="Downside scenario"]/para/text()',
          doc
        );
        const downsideScenario =
          rows.length > 0
            ? rows.reduce((acc, row) => acc + '<p>' + this.decodeHTML(row.nodeValue || '') + '</p>', '')
            : '<p>No downside scenario available</p>';
        report.downsideScenario = downsideScenario;

        rows = xpath.select(
          '/page/ROWSET/ARTICLE/spdoc/analysis/body/section[@name="Outlook"]/section[normalize-space(@name)="Downside scenario"]/list/listitem',
          doc
        );
        report.downsideScenarioCases = rows.map((row) => this.decodeHTML(row.textContent || ''));

        // upside scenario
        rows = xpath.select(
          '/page/ROWSET/ARTICLE/spdoc/analysis/body/section[@name="Outlook"]/section[normalize-space(@name)="Upside scenario"]/para/text()',
          doc
        );
        const upsideScenario =
          rows.length > 0
            ? rows.reduce((acc, row) => acc + '<p>' + this.decodeHTML(row.nodeValue || '') + '</p>', '')
            : '<p>No upside scenario available</p>';
        report.upsideScenario = upsideScenario;

        rows = xpath.select(
          '/page/ROWSET/ARTICLE/spdoc/analysis/body/section[@name="Outlook"]/section[normalize-space(@name)="Upside scenario"]/list/listitem',
          doc
        );
        report.upsideScenarioCases = rows.map((row) => this.decodeHTML(row.textContent || ''));
        return report;
        
      } catch (err) {
        console.error('Error fetching research:', err);
        return this.createEmptyReport();
      }
    },
    
    createEmptyReport() {
      return {
        summary: '<p>No research report available.</p>',
        upsideScenario: '<p>No upside scenario available.</p>',
        downsideScenario: '<p>No downside scenario available.</p>',
        upsideScenarioCases: [],
        downsideScenarioCases: [],
        keys: { errMsg: 'No research report available.' }
      };
    },
    
    formatYear(date) {
      try {
        return date && date.format ? date.format('YYYY') : 'N/A';
      } catch {
        return 'N/A';
      }
    },
    
    formatNumber(value) {
      if (value == null || isNaN(value)) return 'N/A';
      const num = parseFloat(value);
      if (num >= 1e9) return `$${(num / 1e9).toFixed(2)}B`;
      if (num >= 1e6) return `$${(num / 1e6).toFixed(2)}M`;
      if (num >= 1e3) return `$${(num / 1e3).toFixed(2)}K`;
      return `$${num.toFixed(2)}`;
    },
    
    formatPercentage(value) {
      if (value == null || isNaN(value)) return 'N/A';
      return `${parseFloat(value).toFixed(2)}%`;
    },
    filteredKeyStrengths(keys) {
      if (!Array.isArray(keys)) return [];
      return keys.filter(k => k.keyStrength && k.keyStrength.trim());
    },
    filteredKeyRisks(keys) {
      if (!Array.isArray(keys)) return [];
      return keys.filter(k => k.keyRisk && k.keyRisk.trim());
    }
  }
};
</script>

<style scoped>
.mobile-container { padding: 8px; font-size:13px }
.search { display:flex; gap:6px; margin-bottom:8px }
input { flex:1; padding:6px; border:1px solid #ccc; border-radius:4px; font-size:13px }
button{ padding:6px 10px; font-size:13px }
.loader{ color:#666 }
.error{ color:#b00 }
.sections {
  margin-top: 12px;
}
.card{ margin-bottom:12px; padding:12px; border-radius:8px; background:#fff; box-shadow:0 2px 4px rgba(0,0,0,0.1); border:1px solid #e0e0e0; }
.card h3 {
  margin-top: 0;
  margin-bottom: 12px;
  font-size: 16px;
  color: #07202a;
}
.sub{ color:#444; font-size:12px; margin-top:4px }

.company-tags {
  display: flex;
  flex-wrap: wrap;
  gap: 8px;
}

.company-tag {
  background: #e6f2ff;
  padding: 6px 12px;
  border-radius: 16px;
  font-size: 13px;
  color: #005a9e;
}

.comparison-section {
  display: flex;
  flex-direction: column;
  gap: 20px;
}

.company-report {
  padding: 12px;
  border: 1px solid #e6e9ec;
  border-radius: 8px;
  background: #f9fafc;
}

.company-report h4 {
  margin-top: 0;
  margin-bottom: 8px;
  font-size: 15px;
  color: #005a9e;
}

.report-section {
  margin-bottom: 16px;
}

.report-section:last-child {
  margin-bottom: 0;
}

.report-section h5 {
  margin-top: 8px;
  margin-bottom: 4px;
  font-size: 13px;
  color: #333;
}

.report-section h5 {
  margin-top: 8px;
  margin-bottom: 4px;
  font-size: 13px;
  color: #333;
}

.report-section .content {
  font-size: 13px;
  line-height: 1.4;
  color: #333;
}

.report-section ul {
  margin: 8px 0 0 0;
  padding-left: 20px;
}

.report-section li {
  font-size: 13px;
  line-height: 1.4;
  margin-bottom: 4px;
}

.compact-table-wrapper {
  overflow-x: auto;
  -webkit-overflow-scrolling: touch;
}

.compact-table {
  width: 100%;
  border-collapse: collapse;
  font-size: 13px;
}

.compact-table th,
.compact-table td {
  padding: 8px;
  border: 1px solid #e6e9ec;
  text-align: left;
}

.compact-table th {
  background: #f6f7f9;
  font-weight: 600;
  text-align: center;
}

.compact-table td.number {
  text-align: right;
  font-variant-numeric: tabular-nums;
}

.header-ticker {
  font-size: 14px;
  font-weight: 600;
  color: #005a9e;
  margin-bottom: 2px;
}
.header-fy {
  font-size: 11px;
  color: #666;
  font-weight: normal;
}
</style>
